import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminSubscription } from '../../api/admin';
import { confirm } from '../../components/ConfirmDialog';

const fmt = (n: number) => '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
const MODES = ['upi', 'cash', 'bank_transfer', 'cheque', 'other'];
const MODE_LABELS: Record<string, string> = { upi: 'UPI', cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };

const today = () => new Date().toISOString().slice(0, 10);
const in1Year = () => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); };

const emptyForm = () => ({ clientId: '', plan: 'premium', amount: 0, paymentMode: 'upi', paymentDate: today(), expiresAt: in1Year(), notes: '' });

export default function AdminSubscriptionsPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: subs = [], isLoading } = useQuery({ queryKey: ['admin-subscriptions'], queryFn: () => adminApi.listSubscriptions(), staleTime: 30 * 1000 });
  const { data: clients = [] } = useQuery({ queryKey: ['admin-clients', ''], queryFn: () => adminApi.listClients(), staleTime: 30 * 1000 });
  const del = useMutation({ mutationFn: adminApi.deleteSubscription, onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-subscriptions'] }); qc.invalidateQueries({ queryKey: ['admin-dashboard'] }); } });

  const totalRevenue = subs.reduce((s, x) => s + x.amount, 0);
  const activeCount = subs.filter(x => x.expiresAt >= today()).length;
  const now = today();

  async function handleSave() {
    if (!form.clientId) { setError('Please select a client'); return; }
    if (!form.amount || form.amount <= 0) { setError('Amount must be greater than zero'); return; }
    setError(null); setSaving(true);
    try {
      await adminApi.createSubscription({ ...form, amount: Number(form.amount) });
      qc.invalidateQueries({ queryKey: ['admin-subscriptions'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
      setModal(false); setForm(emptyForm());
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Subscriptions</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>
            <span style={{ fontWeight: 700, color: 'var(--success)' }}>{fmt(totalRevenue)}</span> total ·{' '}
            <span style={{ fontWeight: 700 }}>{activeCount}</span> active
          </div>
          <button className="btn btn-primary" onClick={() => { setForm(emptyForm()); setError(null); setModal(true); }}>+ Record Payment</button>
        </div>
      </div>

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : subs.length === 0
          ? <div className="empty-state"><div className="empty-state-icon">💳</div><div className="empty-state-text">No subscription payments recorded yet</div></div>
          : (
            <table>
              <thead><tr><th>Client</th><th>Plan</th><th>Mode</th><th style={{ textAlign: 'right' }}>Amount</th><th>Payment Date</th><th>Expires</th><th>Status</th><th style={{ width: 80 }}>Actions</th></tr></thead>
              <tbody>
                {subs.map((s: AdminSubscription) => {
                  const active = s.expiresAt >= now;
                  return (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.client?.business ?? '—'}</td>
                      <td><span className="badge badge-blue">{s.plan}</span></td>
                      <td style={{ fontSize: '0.83rem' }}>{MODE_LABELS[s.paymentMode] ?? s.paymentMode}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{fmt(s.amount)}</td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--text-2)' }}>{s.paymentDate.slice(0, 10)}</td>
                      <td style={{ fontSize: '0.83rem', color: active ? 'var(--text-2)' : 'var(--danger)' }}>{s.expiresAt.slice(0, 10)}</td>
                      <td><span className={`badge ${active ? 'badge-paid' : 'badge-overdue'}`}>{active ? 'Active' : 'Expired'}</span></td>
                      <td>
                        <button className="btn btn-danger btn-sm" onClick={async () => { if (await confirm({ message: 'Delete this payment record?', danger: true })) del.mutate(s.id); }}>Del</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <span className="modal-title">Record Subscription Payment</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box">{error}</div>}
              <div className="form-row">
                <label>Client *
                  <select value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}>
                    <option value="">— Select Client —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.business} ({c.name})</option>)}
                  </select>
                </label>
              </div>
              <div className="form-row form-row-2">
                <label>Plan
                  <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </label>
                <label>Amount (₹) *
                  <input type="number" min={0} step={1} value={form.amount || ''} onChange={e => setForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} placeholder="2000" />
                </label>
              </div>
              <div className="form-row form-row-3">
                <label>Payment Mode
                  <select value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}>
                    {MODES.map(m => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                  </select>
                </label>
                <label>Payment Date<input type="date" value={form.paymentDate} onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))} /></label>
                <label>Expires On<input type="date" value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} /></label>
              </div>
              <label style={{ fontWeight: 500, fontSize: '0.875rem', display: 'block' }}>Notes
                <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Annual renewal" style={{ marginTop: '0.35rem' }} />
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
